import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import TotalStudentChart from "./TotalStudentChart";
import SubjectAchievementChart from "./SubjectAchievementChart";
import TopicsComparisonTable from "./TopicsComparisonTable";


const SubjectDetailsPage = () => {
  const { programId, subjectId } = useParams();
  const [seriesData, setSeriesData] = useState([]);
  const [years, setYears] = useState([]);

  const [taskSeries, setTaskSeries] = useState([]);
  const [taskYears, setTaskYears] = useState([]);
  const [taskNames, setTaskNames] = useState([]);

  const [methodSeries, setMethodSeries] = useState([]);
  const [methodYears, setMethodYears] = useState([]);
  const [methodNames, setMethodNames] = useState([]);

  const [studentsByYear, setStudentsByYear] = useState({});
  const [totalStudents, setTotalStudents] = useState(0);

  const [achievementData, setAchievementData] = useState({});
  const [overallAvgAchievement, setOverallAvgAchievement] = useState(0);

  const [topics, setTopics] = useState([]);
  const [yearsTopics, setYearsTopics] = useState([]);

  const fetchCloComparison = async () => {
    try {
      const res = await fetch(
        `https://plo.areez.co/api/clo/comparison/subject?program_id=${programId}&subject_id=${subjectId}&last_n=5`
      );
      const json = await res.json();

      const cloCodes = Object.keys(json);
      const allYearsSet = new Set();

      cloCodes.forEach((clo) => {
        Object.keys(json[clo].years).forEach((yr) => allYearsSet.add(yr));
      });

      const sortedYears = Array.from(allYearsSet).sort();
      setYears(cloCodes);

      const yearSeries = sortedYears.map((year) => ({
        name: year,
        type: "bar",
        data: cloCodes.map((clo) => json[clo].years[year] ?? 0),
      }));

      const performanceCriteriaSeries = {
        name: "الحد الأدنى للأداء",
        type: "line",
        data: cloCodes.map((clo) => json[clo].performance_criteria ?? 0),
      };

      const performanceTargetSeries = {
        name: "الهدف المستهدف",
        type: "line",
        data: cloCodes.map((clo) => json[clo].performance_target ?? 0),
      };

      setSeriesData([
        ...yearSeries,
        performanceCriteriaSeries,
        performanceTargetSeries,
      ]);
    } catch (err) {
      console.error("فشل تحميل بيانات CLO:", err);
    }
  };

  const fetchTaskComparison = async () => {
    try {
      const res = await fetch(
        `https://plo.areez.co/api/clo/subject/tasks/comparison?program_id=${programId}&subject_id=${subjectId}&last_n=5`
      );
      const json = await res.json();

      const taskKeys = Object.keys(json); // ← أسماء المهام
      const allYearsSet = new Set();

      taskKeys.forEach((task) => {
        Object.keys(json[task]).forEach((yr) => allYearsSet.add(yr));
      });

      const sortedYears = Array.from(allYearsSet).sort();
      setTaskYears(sortedYears);
      setTaskNames(taskKeys); // 👈 احفظ أسماء المهام

      const taskData = sortedYears.map((year) => ({
        name: year,
        type: "bar",
        data: taskKeys.map((task) => json[task][year] ?? 0),
      }));

      setTaskSeries(taskData);
    } catch (err) {
      console.error("فشل تحميل بيانات المهام:", err);
    }
  };

  const fetchMethodsComparison = async () => {
    try {
      const res = await fetch(
        `https://plo.areez.co/api/clo/subject/methods/comparison?program_id=${programId}&subject_id=${subjectId}&last_n=5`
      );
      const json = await res.json();

      const methodKeys = Object.keys(json); // ← أسماء طرق القياس
      const allYears = new Set();

      methodKeys.forEach((m) => {
        Object.keys(json[m]).forEach((yr) => allYears.add(yr));
      });

      const sorted = Array.from(allYears).sort();
      setMethodYears(sorted);
      setMethodNames(methodKeys); // 👈 حفظ أسماء طرق القياس

      const dataSeries = sorted.map((year) => ({
        name: year,
        type: "bar",
        data: methodKeys.map((m) => json[m][year] ?? 0),
      }));

      setMethodSeries(dataSeries);
    } catch (err) {
      console.error("فشل جلب بيانات طرق القياس:", err);
    }
  };

  const fetchStudentCounts = async () => {
    try {
      fetch(
        `https://plo.areez.co/api/clo/subject/students?program_id=${programId}&subject_id=${subjectId}`
      )
        .then((res) => res.json())
        .then((json) => {
          setStudentsByYear(json.students_by_year);
          setTotalStudents(json.total_students);
        });
    } catch (err) {
      console.error("فشل جلب بيانات عدد الطلاب :", err);
    }
  };

  const fetchStudentAchievemen = async () => {
    try {
      fetch(
        `https://plo.areez.co/api/clo/subject/achievement?program_id=${programId}&subject_id=${subjectId}`
      )
        .then((res) => res.json())
        .then((json) => {
          setAchievementData(json.achievement_by_year || {});
          setOverallAvgAchievement(json.overall_average_clo_achievement || 0);
        });
    } catch (err) {
      console.error("فشل جلب بيانات طرق القياس:", err);
    }
  };

  const fetchTopicsComparison = async () => {
    try {
      const res = await fetch(
        `https://plo.areez.co/api/clo/subject/topics/comparison?program_id=${programId}&subject_id=${subjectId}&last_n=5&page=1&per_page=10`
      );
      const json = await res.json();
      setTopics(json.topics);

      const yearSet = new Set();
      json.topics.forEach((topic) => {
        Object.keys(topic.achievement_by_year).forEach((yr) => yearSet.add(yr));
      });

      setYearsTopics(Array.from(yearSet).sort());
    } catch (err) {
      console.error("فشل تحميل مقارنة المواضيع:", err);
    }
  };

  useEffect(() => {
    fetchCloComparison();
    fetchTaskComparison();
    fetchMethodsComparison();
    fetchStudentCounts();
    fetchStudentAchievemen();
    fetchTopicsComparison();
  }, [programId, subjectId]);

  const getChartOptions = (title) => ({
    chart: {
      height: 350,
      stacked: false,
      toolbar: { show: false },
      type: "line",
    },
    stroke: {
      width: [0, 0, 0, 4, 4],
    },
    plotOptions: {
      bar: {
        columnWidth: "30%",
      },
    },
    xaxis: {
      categories: years,
    },
    yaxis: {
      max: 100,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    legend: {
      position: "bottom",
    },
  });

  const getTaskChartOptions = () => ({
    chart: {
      height: 350,
      stacked: false,
      toolbar: { show: false },
      type: "bar",
    },
    colors: ["#1E90FF", "#FF5733", "#28B463", "#9B59B6", "#FFC300"],
    plotOptions: {
      bar: {
        columnWidth: "30%",
      },
    },
    xaxis: {
      categories: taskNames, // 👈 هنا تظهر أسماء المهام
      labels: {
        rotate: -45,
      },
    },
    yaxis: {
      max: 100,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    legend: {
      position: "bottom",
    },
  });

  const getMethodChartOptions = () => ({
    chart: {
      height: 350,
      stacked: false,
      toolbar: { show: false },
      type: "bar",
    },
    colors: ["#FF5733", "#C70039", "#900C3F", "#581845", "#1C1C1C"],
    plotOptions: {
      bar: { columnWidth: "32%" },
    },
    xaxis: {
      categories: methodNames,
      labels: { rotate: -45 },
    },
    yaxis: {
      max: 100,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    legend: {
      position: "bottom",
    },
  });

  return (
    <>
      <div class="row">
        <div className="col-lg-6 col-sm-6">
          <div className="widget-stat card bg-primary">
            <div className="card-header border-0 pb-0">
              <h3 className="card-title text-white">
                مقارنة عدد الطلاب في المقرر اخر خمس سنوات
              </h3>
              <h5 className="text-white mb-0">{totalStudents}</h5>
            </div>
            <div className="card-body text-center">
              <div className="ico-sparkline">
                <TotalStudentChart studentsByYear={studentsByYear} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 col-sm-6">
          <div className="widget-stat card bg-warning overflow-hidden">
            <div className="card-header border-0">
              <h3 className="card-title text-white">متوسط نسبة تحقيق المقرر</h3>
              <h5 className="text-white mb-0">
                {overallAvgAchievement.toFixed(1)}%
              </h5>
            </div>
            <div className="card-body text-center p-0">
              <div className="ico-sparkline">
                <SubjectAchievementChart achievementByYear={achievementData} />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5 col-12">
          <h5 className="text-center mb-3">
            مقارنة تحقيق نواتج تعلم المقرر في آخر خمس سنوات
          </h5>
          <ReactApexChart
            options={getChartOptions("تحقيق CLO حسب السنوات")}
            series={seriesData}
            type="bar"
            height={350}
          />
        </div>
        <div className="mb-5 col-12">
          <h5 className="text-center mb-3">
            مقارنة نسبة تحقيق مهام القياس في المقرر في آخر خمس سنوات
          </h5>
          <ReactApexChart
            options={getTaskChartOptions()}
            series={taskSeries}
            type="bar"
            height={350}
          />
        </div>

        <div className="mb-5 col-12">
          <h5 className="text-center mb-3">
            مقارنة طرق القياس في المقرر في آخر خمس سنوات
          </h5>
          <ReactApexChart
            options={getMethodChartOptions()}
            series={methodSeries}
            type="bar"
            height={350}
          />
        </div>

        <div className="col-12 mt-5">

          <TopicsComparisonTable programId={programId} subjectId={subjectId} />
        </div>
      </div>
    </>
  );
};

export default SubjectDetailsPage;
